// ---------------------------------------------------------------------------
// Before an upload: is the file already in the deal's or project's OneDrive folder (saved there directly)?
// The browser makes OneDrive's fingerprint of each file, the CRM compares it with every file in the folder, and the
// person chooses: use the one in OneDrive, upload anyway (a separate copy), or cancel. Nothing in OneDrive changes.
// ---------------------------------------------------------------------------
import { api } from './api';
import { formatDateTime } from './format';
import { quickXorOf } from './quickXor';

export interface DupMatch { itemId: string; name: string; path: string; size: number; modifiedAt: string | null; modifiedBy: string | null; same: boolean }
export type DupChoice = 'use' | 'upload' | 'cancel';

/** Files larger than this are compared by name only (making the fingerprint would take too long). */
const HASH_LIMIT = 300 * 1024 * 1024;

/** What is already in the folder for each file (same order); null when the check couldn't be made. */
export async function checkDuplicates(url: string, files: File[]): Promise<DupMatch[][] | null> {
  try {
    const list = [];
    for (const f of files.slice(0, 300)) list.push({ name: f.name, size: f.size, hash: f.size <= HASH_LIMIT ? await quickXorOf(f) : null });
    const r = await api<{ checked: boolean; results: DupMatch[][] }>(url, { method: 'POST', body: { files: list }, quiet: true });
    return r.checked ? r.results : null;
  } catch { return null; }
}

/** The question, in a small window: resolves with the person's choice. */
export function askDuplicate(file: File, matches: DupMatch[], canUse: boolean): Promise<DupChoice> {
  return new Promise((resolve) => {
    const same = matches.find((m) => m.same);
    const shown = same ? [same] : matches.slice(0, 3);
    const back = document.createElement('div');
    back.className = 'dup-backdrop';
    const box = document.createElement('div');
    box.className = 'dup-box';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    const h = document.createElement('h3');
    h.textContent = same ? 'Already in OneDrive' : 'A file with this name is already in OneDrive';
    const p = document.createElement('p');
    p.textContent = same
      ? `“${file.name}” is the same file as:`
      : `“${file.name}” has the same name as the file${shown.length > 1 ? 's' : ''} below, but different content (probably another version):`;
    const ul = document.createElement('ul');
    for (const m of shown) {
      const li = document.createElement('li');
      const b = document.createElement('b');
      b.textContent = m.path;
      const s = document.createElement('span');
      s.className = 'small muted';
      s.textContent = [m.modifiedAt ? `saved ${formatDateTime(m.modifiedAt)}` : '', m.modifiedBy ? `by ${m.modifiedBy}` : ''].filter(Boolean).join(' ');
      li.append(b, document.createElement('br'), s);
      ul.append(li);
    }
    const actions = document.createElement('div');
    actions.className = 'dup-actions';
    const done = (c: DupChoice) => { back.remove(); document.removeEventListener('keydown', esc); resolve(c); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); done('cancel'); } };
    const button = (label: string, choice: DupChoice, primary = false) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `btn${primary ? ' primary' : ''}`;
      btn.textContent = label;
      btn.addEventListener('click', () => done(choice));
      actions.append(btn);
      return btn;
    };
    const first = same && canUse ? button('Use the one in OneDrive', 'use', true) : null;
    const up = button(same ? 'Upload anyway (a separate copy)' : 'Upload as a new copy', 'upload', !first);
    button('Cancel', 'cancel');
    const note = document.createElement('p');
    note.className = 'small muted';
    note.textContent = 'Nothing in OneDrive is changed or replaced.';
    box.append(h, p, ul, actions, note);
    back.append(box);
    back.addEventListener('click', (e) => { if (e.target === back) done('cancel'); });
    document.addEventListener('keydown', esc);
    document.body.append(back);
    (first ?? up).focus();
  });
}
